import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setCredentials } from '../../store/authSlice';
import { verifyOtp, requestOtp } from '../../api/auth.api';
import AuthLayout from '../../layouts/AuthLayout';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';

const OTPVerify = () => {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(60);
  const [resending, setResending] = useState(false);
  const refs = useRef([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const otpRequest = useSelector((s) => s.auth.otpRequest);
  const email = otpRequest?.email;

  useEffect(() => {
    if (!email) navigate('/login');
  }, [email, navigate]);

  useEffect(() => {
    if (resendCountdown === 0) return;
    const t = setInterval(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [resendCountdown]);

  const handleDigit = (i, val) => {
    const v = val.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < 5) refs.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = Array(6).fill('');
    pasted.split('').forEach((ch, i) => { next[i] = ch; });
    setDigits(next);
    refs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i - 1]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otp = digits.join('');
    if (otp.length < 6) { setError('Please enter the complete 6-digit OTP'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await verifyOtp({ email, otp });
      dispatch(setCredentials({ token: res.data.token, user: res.data.user }));
      navigate('/candidate/exams');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired OTP');
      setDigits(['', '', '', '', '', '']);
      refs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!otpRequest) return;
    setResending(true);
    try {
      // Backend's requestOtpSchema requires `mobile` too — resend with the
      // full original payload, not just email, otherwise Joi returns 422.
      await requestOtp(otpRequest);
      setResendCountdown(60);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setResending(false);
    }
  };

  const filled = digits.filter(Boolean).length;

  return (
    <AuthLayout>
      <div className="mb-7">
        <div className="inline-flex items-center gap-2 bg-primary-50 text-primary-700 px-3 py-1 rounded-full text-xs font-semibold mb-3">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Email Verification
        </div>
        <h2 className="text-2xl font-bold text-slate-900 leading-tight">Enter your OTP</h2>
        <p className="text-slate-500 text-sm mt-1">
          Code sent to <span className="font-medium text-slate-700">{email}</span>
        </p>
      </div>

      {error && <Alert type="error" className="mb-5">{error}</Alert>}

      <form onSubmit={handleSubmit}>
        {/* OTP Digit inputs */}
        <div className="flex gap-2.5 justify-center mb-6">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (refs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => handleDigit(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={i === 0 ? handlePaste : undefined}
              className={`
                w-12 h-14 text-center text-xl font-bold rounded-xl border-2 transition-all duration-150
                focus:outline-none
                ${d
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-slate-200 bg-white text-slate-900 hover:border-slate-300'
                }
                focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10
              `}
            />
          ))}
        </div>

        {/* Progress indicator */}
        <div className="flex gap-1 justify-center mb-6">
          {Array(6).fill(null).map((_, i) => (
            <div
              key={i}
              className={`h-1 w-6 rounded-full transition-all duration-200 ${
                i < filled ? 'bg-primary-500' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>

        <Button type="submit" fullWidth size="lg" loading={loading} disabled={filled < 6}>
          {loading ? 'Verifying…' : 'Verify OTP'}
        </Button>
      </form>

      <div className="text-center mt-5">
        {resendCountdown > 0 ? (
          <p className="text-sm text-slate-400">
            Resend OTP in{' '}
            <span className="font-semibold text-slate-600">{resendCountdown}s</span>
          </p>
        ) : (
          <button
            onClick={handleResend}
            disabled={resending}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium hover:underline disabled:opacity-50"
          >
            {resending ? 'Sending…' : 'Resend OTP'}
          </button>
        )}
      </div>
    </AuthLayout>
  );
};

export default OTPVerify;
